import { React } from "react";
import { graphql } from "gatsby";
import { Container } from "theme-ui";
import Layout from "@solid-ui-layout/Layout";
import Seo from "@solid-ui-components/Seo";
import Divider from "@solid-ui-components/Divider";
import ModalWithTabs from "@solid-ui-blocks/Modal/Block01";
import ModalSimple from "@solid-ui-blocks/Modal/Block02";
import Header from "@solid-ui-blocks/Header/Block01";
import Tabs from "@solid-ui-components/Tabs";
import Hero from "@solid-ui-blocks/Hero/Block01";
import FeatureOne from "@solid-ui-blocks/FeaturesWithPhoto/Block02";
import FeatureTwo from "@solid-ui-blocks/FeaturesWithPhoto/Block05";
import FeatureThree from "@solid-ui-blocks/FeaturesWithPhoto/Block01";
import Features from "@solid-ui-blocks/Features/Block02";
import Process from "@solid-ui-blocks/Features/Block03";
import Testimonials from "@solid-ui-blocks/Testimonials/Block03";
import Companies from "@solid-ui-blocks/Companies/Block01";
import Stats from "@solid-ui-blocks/Stats/Block01";
import Faq from "@solid-ui-blocks/Faq/Block01";
import Blog from "@solid-ui-blocks/Blog/Block01";
import Footer from "@solid-ui-blocks/Footer/Block01";
import { normalizeBlockContentNodes } from "@blocks-helpers";
import theme from "./_theme";
import styles from "./_styles";
import { format } from "date-fns";

const IndexPage = (props) => {
  const blogHost = "https://blog.schedwise.com/";
  const { allBlockContent } = props.data;

  const content = normalizeBlockContentNodes(allBlockContent?.nodes);

  const posts = {
    text: [
      {
        text: "Latest From Our Blog",
        variant: "h2",
      },
      {
        text: "Stay updated with the latest trends, tips, and stories from our blog.",
        variant: "medium",
      },
    ],

    collection: props.data.posts.edges.map((node) => {
      var postInfo = node.node;
      return {
        container: {
          variant: "cards.interactive",
          link: {
            type: "PAGE",
            link: blogHost + postInfo.frontmatter.slug + "/",
          },
        },
        text: [
          {
            text: postInfo.frontmatter.category,
            color: "alphaDark",
          },
          {
            text: postInfo.frontmatter.title,
            color: "omegaDarker",
            variant: "h5",
          },
          {
            text: postInfo.body
              .replace(/(#{1,6})\s+/g, "")
              // Remove Markdown links
              // Remove Markdown images
              .replace(/!\[.*?\]\(.*?\)/g, "")
              // Remove inline code
              .replace(/`([^`]+)`/g, "$1")
              // Remove code blocks (both fenced and indented)
              .replace(/```[\s\S]*?```/g, "")
              .replace(/`{3}[^`]*`{3}/g, "") // Removing fenced code blocks (triple backticks)
              // Remove HTML tags (including JSX components)
              .replace(/<[^>]+>/g, "")
              .slice(0, 300), // Remove all HTML tags
            // Remove empty lines or extra spaces
          },
          {
            text: postInfo.frontmatter.author,
            color: "omegaDark",
          },
          {
            text: format(new Date(postInfo.frontmatter.date), "MMMM d, yyyy"),
          },
          {
            text: "",
          },
        ],
        avatar: {
          src: "../../../assets/flexiblocks/testimonials/sarah-doe.png",
          alt: "alt text",
        },
        link: blogHost + postInfo.frontmatter.slug + "/",
        authorLink:
          blogHost +
          "author/" +
          postInfo.frontmatter.author
            .normalize("NFD") // Normalize the string into decomposed Unicode
            .replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks
            .replace(/%20/g, "-") // Replace encoded spaces
            .replace(/\s+/g, "-") // Replace regular spaces with hyphens
            .toLowerCase() // Convert to lowercase
            .replace(/[^a-z0-9-]/g, "") // Remove non-alphanumeric and non-hyphen characters
            .replace(/^-+|-+$/g, ""),
        categoryLink:
          blogHost +
          "category/" +
          postInfo.frontmatter.category
            .toLowerCase() // Convert to lowercase
            .replace(/\s*&\s*/g, "-and-") // Replace " & " (or similar) with "-and-"
            .replace(/\s+/g, "-") // Replace spaces with hyphens
            .replace(/[^a-z0-9-]/g, "") // Remove non-alphanumeric and non-hyphen characters
            .replace(/^-+|-+$/g, ""),
        images: [
          {
            src: postInfo.frontmatter.thumbnail.childImageSharp.gatsbyImageData
              .images.fallback.src,
            alt: postInfo.title,
          },
        ],
      };
    }),
  };

  return (
    <Layout theme={theme} {...props}>
      <Seo
        title="One AI Agent. Every Channel. More Bookings."
        thumbnail="https://schedwise.com/link-placeholder.png"
      />
      {/* Modals */}
      <ModalWithTabs content={content["authentication"]} reverse />
      <ModalWithTabs content={content["contact"]} />
      <ModalSimple content={content["advertisement"]} />
      {/* Blocks */}
      <Header content={content["header-light"]} menuJustify="space-between" />
      <Divider space="5" />
      <Divider space="5" />
      <Container variant="full" sx={styles.heroContainer}>
        <Hero content={content["hero"]} reverse />
      </Container>
      <Container variant="wide" sx={styles.tabsContainer}>
        <Divider space="3" />
        <Tabs space={4}>
          <FeatureOne content={content["tab-feature-one"]} />
          <FeatureTwo content={content["tab-feature-two"]} />
          <FeatureThree content={content["tab-feature-three"]} />
        </Tabs>
      </Container>
      <Divider space="5" />
      <Companies content={content["companies"]} />
      <Divider space="5" />
      <Divider space="5" />
      <Process content={content["process"]} />
      <Divider space="5" />
      <Container variant="full" sx={styles.featuresContainer}>
        <Features content={content["features"]} />
      </Container>
      <Divider space="5" />
      <Container variant="full" sx={styles.socialProofContainer}>
        <Divider space="5" />
        <Divider space="5" />
        <Container variant="narrow">
          <Stats content={content["stats"]} />
          <Divider space="5" />
        </Container>
        <Divider space="5" />
      </Container>
      <Divider space="5" />
      <Container variant="narrow">
        <Faq content={content["faq"]} />
      </Container>
      <Divider space="5" />
      <Blog content={posts} />
      <Divider space="5" />
      <Footer content={content["footer"]} />
    </Layout>
  );
};

export const query = graphql`
  query homepageSaasV2BlockContent {
    allBlockContent(filter: { page: { in: ["homepage/saas-v2", "shared"] } }) {
      nodes {
        ...BlockContent
      }
    }
    posts: allMdx(sort: { frontmatter: { date: DESC } }, limit: 3) {
      edges {
        node {
          id
          frontmatter {
            title
            author
            category
            tags
            date
            slug
            thumbnail {
              childImageSharp {
                gatsbyImageData(width: 600)
              }
              publicURL
            }
          }
          body
        }
      }
    }
  }
`;
export default IndexPage;
